import React, { useMemo } from "react";
import { Box, Alert } from "@mui/material";
import { useStoreDetails } from "../../../../common/Providers/StoreDetailsProvider/UseStoreDetails";
import { useVehicleReservation } from "../../../../common/Providers/VehicleReservationProvider/UseVehicleReservation";
import FormSelect from "../../../ui/FormSelect";

interface DurationSelectorProps {
  dateData: any;           // The selected date's data from API response
  apiData: any;            // Full API response (for business_hours, daily_return_time, etc.)
  onDurationSelect: (durationData: any) => void;
  selectedDurationObj: any;
}

const DurationSelector: React.FC<DurationSelectorProps> = ({ dateData, apiData, onDurationSelect, selectedDurationObj }) => {
  const { storeContent } = useStoreDetails();
  const { VehicleReservationItems } = useVehicleReservation();

  // Build duration options from API response
  const durationOptions = useMemo(() => {
    if (!dateData?.durations) return [];

    // Map durations and deduplicate by id (duration_value + duration_type)
    const durationMap = new Map();
    
    dateData.durations.forEach((d: any) => {
      let id = '';
      if (d.duration_type === 'hourly') {
        id = `${d.duration_value}-hour`;
      } else if (d.duration_type === 'daily') {
        id = `${d.duration_value}-day`;
      } else if (d.duration_type === 'weekly') {
        id = `${d.duration_value}-week`;
      } else if (d.duration_type === 'monthly') {
        id = `${d.duration_value}-month`;
      }

      // Only add if not already in map, or if this one is available and existing isn't
      if (!durationMap.has(id) || (d.is_available && !durationMap.get(id).is_available)) {
        durationMap.set(id, {
          ...d,
          id,
          name: d.duration_label || `${d.duration_value} ${d.duration_type}`,
          price: parseFloat(d.price) || 0,
        });
      }
    });

    return Array.from(durationMap.values());
  }, [dateData]);

  // Disabled durations: those where is_available is false
  const disabledDurationIds = useMemo(() => {
    return new Set(
      durationOptions.filter((d: any) => !d.is_available).map((d: any) => d.id)
    );
  }, [durationOptions]);

  const availableDurationsCount = durationOptions.filter((d: any) => d.is_available).length;
  const selectedValue = selectedDurationObj?.id || '';

  // Determine label based on selection
  const selectLabel = selectedDurationObj 
    ? `${selectedDurationObj.name} (${selectedDurationObj.price.toFixed(2)})`
    : `${availableDurationsCount} durations available`;

  // Show message when no durations are available
  if (availableDurationsCount === 0) {
    const itemCount = VehicleReservationItems.length;
    const itemText = itemCount === 1 ? 'item is' : 'items are';
    
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 2 }}>
          The selected {itemText} not available for booking on this date. Please try a different date or reduce the quantity.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <FormSelect
        label={selectLabel}
        value={selectedValue}
        options={durationOptions}
        getOptionValue={(opt: any) => opt.id}
        getOptionLabel={(opt: any) => `${opt.name} (${opt.price.toFixed(2)})`}
        getOptionPostfix={(opt: any) => {
          if (disabledDurationIds.has(opt.id)) {
            return <span style={{ color: '#f44336' }}>(sold out)</span>;
          }
          // Upcharge is already included in the price, no need to display separately
          // if (opt.upcharge && opt.upcharge > 0) {
          //   return (
          //     <span style={{ 
          //       fontSize: '0.85em', 
          //       color: '#666', 
          //       marginLeft: 'auto',
          //       whiteSpace: 'nowrap'
          //     }}>
          //       +${opt.upcharge.toFixed(2)} extra
          //     </span>
          //   );
          // }
          return null;
        }}
        getOptionDisabled={(opt: any) => disabledDurationIds.has(opt.id)}
        onChange={(_value: any, selected: any) => {
          onDurationSelect(selected || null);
        }}
        primaryColor={storeContent.primary_color}
        clearable={true}
      />
    </Box>
  );
};

export default DurationSelector;
